import React, { useContext, useEffect, useState } from 'react';
import { ConfigProvider, Modal, Table, TableProps, Tag, Input, Button, Space, Switch, message } from 'antd';
import locale from "antd/es/locale/es_ES";
import { AuthContext } from "../../../../config/AuthContext";
import JutgeColors from "../../../../styles/JutgeColors";
import { ColumnsType } from "antd/es/table";
import { GetJutgeProblemes } from "../../../../dao/jutge/GetJutgeProblemes";
import { GetUsuariIdJutgeActivitatsId } from "../../../../dao/GetUsuariIdJutgeActivitatsId";
import { PutJutgeActivitatProblemes } from "../../../../dao/jutge/PutJutgeActivitatProblemes";

interface Props {
    nom: string;
    id_activitat: string;
    visible: boolean;
    onClose: (value: boolean) => void;
}

const ExercicisModal: React.FC<Props> = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dades, setDades] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const { isLoggedIn, userId } = useContext(AuthContext);

    useEffect(() => {
        if (isLoggedIn) checkDades();
    }, [isLoggedIn, props.id_activitat]);

    async function checkDades() {
        setLoading(true);
        try {
            const prob = await GetJutgeProblemes(userId);
            setDades(Array.isArray(prob) ? prob : []);
            const cur = await GetUsuariIdJutgeActivitatsId(userId, props.id_activitat + "");
            const currentList = Array.isArray(cur) ? cur : [];
            setSelectedIds(
                currentList
                    .filter((a: any) => a && a.id != null)
                    .map((a: any) => Number(a.id))
            );
        } finally {
            setLoading(false);
        }
    }

    const handleCancel = () => props.onClose(false);

    function marcarEjercicio(record: any, checked: boolean) {
        const idNum = Number(record.id);
        setSelectedIds(prev => {
            if (checked) {
                return prev.includes(idNum) ? prev : [...prev, idNum];
            } else {
                return prev.filter(id => id !== idNum);
            }
        });
    }

    async function handleSave() {
        setSaving(true);
        try {
            await PutJutgeActivitatProblemes(props.id_activitat + "", selectedIds);
            message.success("Exercicis de l'activitat guardats correctament");
            props.onClose(true);
        } catch (err) {
            console.error("Error al guardar exercicis:", err);
            message.error("Error al guardar els exercicis");
        } finally {
            setSaving(false);
        }
    }

    const columns: ColumnsType<any> = [
        {
            key: 'id',
            title: 'ID',
            dataIndex: 'id',
            defaultSortOrder: 'ascend',
        },
        {
            key: 'nom',
            title: 'Nom',
            dataIndex: 'nom',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Buscar por nombre"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Buscar
                        </Button>
                        <Button
                            onClick={() => clearFilters && clearFilters()}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Limpiar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: filtered => <span>🔍</span>,
            onFilter: (value, record) =>
                record.nom.toLowerCase().includes((value as string).toLowerCase()),
        },
        {
            key: 'dificultat',
            title: 'Dificultat',
            dataIndex: 'dificultat',
            filters: [
                { text: 'beginner', value: 'beginner' },
                { text: 'intermediate', value: 'intermediate' },
                { text: 'expert', value: 'expert' },
            ],
            onFilter: (value, record) => record.dificultat === value,
            render: (value: string) => (
                <Tag color={
                    value === 'beginner' ? JutgeColors.colors.color4 :
                        value === 'intermediate' ? JutgeColors.colors.color2 :
                            JutgeColors.colors.color5
                }>
                    {value}
                </Tag>
            ),
        },
        {
            key: 'assignat',
            title: 'Assignat',
            dataIndex: 'assignat',
            render: (_, record) => {
                const estaAssignat = selectedIds.includes(Number(record.id));

                return (
                    <Switch
                        checked={estaAssignat}
                        onChange={(checked: boolean) => {
                            marcarEjercicio(record, checked);
                        }}
                    />
                );
            }
        }
    ];


    const tableProps: TableProps<any> = { loading };

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={"Assignar Exercicis a l'Activitat: " + props.nom}
                open={props.visible}
                onCancel={handleCancel}
                footer={[
                    <Button key="cancelar" onClick={handleCancel} disabled={saving}>
                        Cancel·lar
                    </Button>,
                    <Button
                        key="guardar"
                        type="primary"
                        loading={saving}
                        onClick={handleSave}
                        style={{ backgroundColor: JutgeColors.colors.color3, borderColor: JutgeColors.colors.color3 }}
                    >
                        Guardar ({selectedIds.length})
                    </Button>
                ]}
                width={1000}
            >
                <Table
                    showHeader={true}
                    size="small"
                    title={() => "EXERCICIS"}
                    {...tableProps}
                    bordered
                    columns={columns}
                    dataSource={dades?.sort((a: any, b: any) => a.id - b.id)}
                    rowKey="id"
                />
            </Modal>
        </ConfigProvider>
    );
};

export default ExercicisModal;
